import { useCallback, useMemo } from "react";
import { RefreshCwIcon, Maximize, Copy, MoreVertical, TableIcon, Braces } from "lucide-react";
import { useParams } from "react-router-dom";

import { useRetryReportCell } from "../../hooks/useRetryReportCell";
import { getCellValue } from "../utils";

import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";
import { useReportContext } from "@/components/Evaluate/report-context";
import { DropdownMenu, Action } from "@/components/ui/DropdownMenu";
import {
  useGridContext,
  ViewMode,
} from "@/components/Evaluate/TableComponents/hooks/useEvaluateGridContext";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";
import cakeColor from "@/assets/logo/promptlayer-cake-color.png";

interface UseCellActionsProps {
  cell: ReportCell;
  column: ReportColumn;
  showRetry?: boolean;
  showCopy?: boolean;
  showFullscreen?: boolean;
  showOpenRequest?: boolean;
  showOpenDataset?: boolean;
}

const getOpenRequestUrl = (column: ReportColumn, cell: ReportCell, workspaceId: string) => {
  const isPrompt = column.column_type === ColumnType.PROMPT_TEMPLATE;
  const id = isPrompt ? cell?.value?.value?.request_id : cell?.value?.value?.trace_id;

  if (isPrompt) {
    return `/workspace/${workspaceId}/request/${id}`;
  }

  return `/workspace/${workspaceId}/traces/${id}`;
};

export const useCellActions = ({
  cell,
  column,
  showRetry = true,
  showCopy = true,
  showFullscreen = true,
  showOpenRequest = false,
  showOpenDataset = true,
}: UseCellActionsProps) => {
  const { isEditDisabled } = useRbac();
  const cannotEditReport = isEditDisabled(EntityType.REPORT);

  const { openFullscreenDialog, currentDatasetGroup, reportMetadata } = useReportContext();
  const { workspaceId } = useParams();
  const { tableStore } = useGridContext();
  const isCompactMode = tableStore?.viewMode === ViewMode.COMPACT;
  const shouldShowRetry = showRetry && column.column_type !== ColumnType.DATASET;

  const { retryReportCell, isLoading: retryReportCellIsLoading } = useRetryReportCell(cell?.id);

  const copyValue = useMemo(() => {
    if (column.column_type === ColumnType.HUMAN) {
      return cell?.value?.value;
    }

    return getCellValue(cell, column.column_type);
  }, [cell, column.column_type]);

  const isOutputMedia = useMemo(() => {
    const dv = cell?.display_value;
    if (!dv) {
      return false;
    }
    if (dv.type === "output_media") {
      return true;
    }
    const val = dv.value as { type?: string } | undefined;
    return val?.type === "output_media";
  }, [cell?.display_value]);

  const handleCopy = useCallback(async () => {
    if (copyValue) {
      await navigator.clipboard.writeText(copyValue as string);
    }
  }, [copyValue]);

  const actions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const menuItems: Action<any>[] = [];
    const iconSize = isCompactMode ? "h-3 w-3" : "h-4 w-4";

    if (shouldShowRetry) {
      menuItems.push({
        id: "retry",
        label: "Retry",
        tooltipContent: cannotEditReport ? "You don't have permission to retry cells" : undefined,
        icon: <RefreshCwIcon className="h-5 w-5" />,
        onClick: () => retryReportCell(),
        getDisabled: () => retryReportCellIsLoading || cannotEditReport,
      });
    }

    if (showCopy) {
      menuItems.push({
        id: "copy",
        label: "Copy",
        icon: <Copy className="h-5 w-5" />,
        onClick: handleCopy,
      });
    }

    if (isOutputMedia) {
      menuItems.push({
        id: "viewRawObject",
        label: "View raw object",
        icon: <Braces className="h-5 w-5" />,
        onClick: () => openFullscreenDialog(cell, column, "raw"),
      });
    }

    if (
      showFullscreen &&
      column.column_type !== ColumnType.LLM_ASSERTION &&
      cell?.value?.value !== true
    ) {
      menuItems.push({
        id: "fullscreen",
        label: "Fullscreen",
        icon: <Maximize className="h-5 w-5" />,
        onClick: () => openFullscreenDialog(cell, column),
      });
    }

    if (showOpenRequest && workspaceId) {
      const url = getOpenRequestUrl(column, cell, workspaceId);
      const isPrompt = column.column_type === ColumnType.PROMPT_TEMPLATE;
      const label = isPrompt ? "Open Prompt" : "Open Trace";
      menuItems.push({
        id: "openRequest",
        label,
        icon: <img src={cakeColor} className="h-5 w-5" alt="Open Request" />,
        onClick: () => window.open(url, "_blank"),
      });
    }

    if (
      showOpenDataset &&
      workspaceId &&
      currentDatasetGroup &&
      cell?.dataset_row_id !== undefined &&
      cell?.dataset_row_id !== null
    ) {
      const datasetId = reportMetadata?.report?.dataset_id;
      if (datasetId) {
        const datasetUrl = `/workspace/${workspaceId}/dataset-groups/${currentDatasetGroup.id}/dataset/${datasetId}?row=${cell.dataset_row_id}`;
        menuItems.push({
          id: "openDataset",
          label: "Open in Dataset",
          icon: <TableIcon className="h-5 w-5" />,
          onClick: () => window.open(datasetUrl, "_blank"),
        });
      }
    }

    if (menuItems.length === 0) {
      return [];
    }

    return (
      <DropdownMenu
        menuItems={menuItems}
        contentClassName="!w-fit"
        icon={<MoreVertical className={iconSize} />}
        triggerClassName="!border-[1px] !border-gray-200"
      />
    );
  }, [
    shouldShowRetry,
    workspaceId,
    showOpenRequest,
    showOpenDataset,
    retryReportCellIsLoading,
    showCopy,
    showFullscreen,
    isOutputMedia,
    column,
    cell,
    isCompactMode,
    openFullscreenDialog,
    handleCopy,
    retryReportCell,
    cannotEditReport,
    currentDatasetGroup,
    reportMetadata,
  ]);

  return {
    actions,
    copyValue,
    retryReportCell,
    retryReportCellIsLoading,
  };
};
